import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HSGLogo from '@/components/feature/HSGLogo';

export default function Hero() {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Trigger entrance animation after mount
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full h-screen min-h-[700px] flex items-center justify-center overflow-hidden">
      {/* Background Image — Ken Burns cinematic zoom */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/d360975ea2d1c719f0fc53e46570ab11.jpg"
          alt="Higher Standard Global Premium Properties"
          className={`w-full h-full object-cover object-top transition-transform ease-out ${
            loaded
              ? 'scale-100 duration-[8000ms]'
              : 'scale-110 duration-0'
          }`}
          style={{
            transformOrigin: 'center center',
          }}
        />
        {/* Dark overlay — fades in with content */}
        <div
          className={`absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/65 transition-opacity duration-1000 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </div>

      {/* Content — staggered fade-up entrance */}
      <div className="relative z-10 w-full flex flex-col items-center justify-center text-center px-6">
        {/* Eyebrow Badge */}
        <div
          className={`inline-flex items-center border border-white/30 rounded-full px-5 py-2 mb-8 transition-all duration-700 ease-out ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: loaded ? '300ms' : '0ms' }}
        >
          <span className="text-white/80 text-xs uppercase tracking-[0.2em] font-medium">
            By Reference Only
          </span>
        </div>

        {/* Logo */}
        <div
          className={`mb-8 transition-all duration-700 ease-out ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: loaded ? '500ms' : '0ms' }}
        >
          <HSGLogo variant="light" size="lg" />
        </div>

        {/* Tagline */}
        <p
          className={`text-white/80 text-lg md:text-xl font-light tracking-wide max-w-xl leading-relaxed transition-all duration-700 ease-out ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: loaded ? '700ms' : '0ms' }}
        >
          Curated Property Management for Discerning Owners
        </p>

        {/* CTA Buttons */}
        <div
          className={`mt-10 flex flex-col sm:flex-row items-center gap-4 transition-all duration-700 ease-out ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: loaded ? '900ms' : '0ms' }}
        >
          <button
            onClick={() => navigate('/about')}
            className="bg-[#C9A961] hover:bg-[#B8954F] text-[#1A1A1A] font-semibold text-xs uppercase tracking-[0.2em] px-8 py-3.5 rounded-md transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Discover More
          </button>
          <button
            onClick={() => navigate('/contact')}
            className="border border-white/40 hover:border-white text-white/80 hover:text-white font-medium text-xs uppercase tracking-[0.2em] px-8 py-3.5 rounded-md transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Request Introduction
          </button>
        </div>

        {/* Scroll indicator */}
        <div
          className={`mt-14 flex flex-col items-center gap-2 transition-all duration-700 ease-out ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: loaded ? '1100ms' : '0ms' }}
        >
          <div className="w-px h-10 bg-white/30 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
}
